import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Account, AccountType, Money as MoneyModel, UserPlan } from 'api-client';
import avatar from 'assets/images/svg/avatar.svg';
import rub from 'assets/images/svg/rub-black.svg';
import {
  ArrowIcon,
  CheckIcon,
  Container,
  CustomLink,
  Image,
  Money,
  PlusIcon,
  ProgressBar,
  Section,
  Skeleton,
  Tooltip,
} from 'components';
import { ComfortAccountTopupModal, ComfortAccountWizardModal } from 'components/common/modals';
import { AccountNotReadyWarningSection } from 'components/pages';
import { createAccountManager, createStatsProvider } from 'di';
import { formatDate } from 'formatters';
import { useAppDispatch, useErrorCallback, useMyMoneyData, useProfileData } from 'hooks';
import { cn, RouteDictionary } from 'utils';

import { set as setMoneyData } from '../../../../../store/my-money';
import { MyMoneyPortfolioCard } from './MyMoneyPortfolioCard';
import { MY_MONEY_TOOLTIPS } from './myMoneyTooltips';
import styles from './MyMoneyV2.module.scss';

type PlatformItem = {
  id: string;
  label: string;
  amount: number;
  tooltip: string;
};

const formatRate = (value?: number): string => `${Math.round(Number(value ?? 0))}\xa0%`;

const formatTerm = (value?: number): string => `${Math.round(Number(value ?? 0))}\xa0мес.`;

const COMFORT_FEATURES = [
  'Начисление бонусов на свободный остаток',
  'Заранее известны характеристики проектов',
  'Вход в проекты маленькими суммами',
  'Быстрая диверсификация портфеля',
];

const PREMIUM_PROGRESS_COLOR = '#00703e';

type PlanFeature = {
  id: string;
  content: React.ReactNode;
};

const STANDARD_PLAN_FEATURES: PlanFeature[] = [
  {
    id: 'reservation',
    content: <>Бронирование сделок при наличии денег на счете</>,
  },
  {
    id: 'distribution',
    content: <>Базовое распределение финреза</>,
  },
];

const PREMIUM_PLAN_FEATURES: PlanFeature[] = [
  {
    id: 'reservation',
    content: <>Бронирование сделок без денег на счете</>,
  },
  {
    id: 'distribution-base',
    content: <>Финрез +5% к базовому до 50 млн</>,
  },
  {
    id: 'distribution-extended',
    content: <>Финрез +10% к базовому свыше 50 млн</>,
  },
  {
    id: 'manager',
    content: <>Личный менеджер</>,
  },
];

export const MyMoneyHome = () => {
  const [loading, setLoading] = useState(true);
  const [comfortModalOpen, setComfortModalOpen] = useState(false);
  const [topupModalOpen, setTopupModalOpen] = useState(false);
  const [mainAccount, setMainAccount] = useState<Account | null>(null);
  const [comfortAccount, setComfortAccount] = useState<Account | null>(null);
  const navigate = useNavigate();
  const handleError = useErrorCallback();
  const dispatch = useAppDispatch();
  const didInitRef = useRef(false);
  const handleErrorRef = useRef(handleError);

  const myMoney = useMyMoneyData();
  const profile = useProfileData();

  const comfortAccountOpened = comfortAccount !== null;
  const currencyCode = profile.balance.currencyCode;

  const mainBalance = useMemo(
    () => mainAccount?.balance ?? new MoneyModel(0, currencyCode),
    [mainAccount, currencyCode]
  );

  const comfortBalance = useMemo(
    () => comfortAccount?.balance ?? new MoneyModel(0, currencyCode),
    [comfortAccount, currencyCode]
  );

  const loadAccounts = useCallback(async () => {
    const accountManager = await createAccountManager();
    const [main, comfort] = await Promise.all([
      accountManager.getOne(AccountType.MAIN),
      accountManager.getOne(AccountType.COMFORT),
    ]);
    setMainAccount(main);
    setComfortAccount(comfort);
  }, []);

  const isPremium = profile.userPlan === UserPlan.CONOMICA_PLUS;

  const createdAt = useMemo(() => {
    if (!profile.identificationDate) {
      return null;
    }

    const date = new Date(profile.identificationDate);

    return Number.isNaN(date.getTime()) ? null : formatDate(date);
  }, [profile.identificationDate]);

  const premiumProgressPercent = useMemo(() => {
    const threshold = myMoney.premiumThreshold.amount;
    if (threshold <= 0) {
      return 0;
    }
    return Math.min(100, Math.round((myMoney.premiumInvestmentSum.amount / threshold) * 100));
  }, [myMoney.premiumInvestmentSum.amount, myMoney.premiumThreshold.amount]);

  const premiumProgressLines = useMemo(
    () => [{ filled: premiumProgressPercent, color: PREMIUM_PROGRESS_COLOR }],
    [premiumProgressPercent]
  );

  const premiumRemaining = useMemo(
    () =>
      new MoneyModel(
        Math.max(0, myMoney.premiumThreshold.amount - myMoney.premiumInvestmentSum.amount),
        myMoney.premiumThreshold.currencyCode
      ),
    [myMoney.premiumThreshold, myMoney.premiumInvestmentSum]
  );

  const platformItems = useMemo<PlatformItem[]>(
    () => [
      {
        id: 'available',
        label: 'Доступно',
        amount: mainBalance.amount,
        tooltip: MY_MONEY_TOOLTIPS.available,
      },
      {
        id: 'reserved',
        label: 'Зарезервировано',
        amount: myMoney.total.reservedInvestment.amount,
        tooltip: MY_MONEY_TOOLTIPS.reserved,
      },
      {
        id: 'invested',
        label: 'Инвестировано',
        amount: myMoney.current.invested.amount,
        tooltip: MY_MONEY_TOOLTIPS.currentInvested,
      },
    ],
    [mainBalance.amount, myMoney.total.reservedInvestment.amount, myMoney.current.invested.amount]
  );

  const platformTotal = useMemo(
    () => ({
      amount: platformItems.reduce((sum, item) => sum + item.amount, 0),
      currencyCode,
    }),
    [platformItems, currencyCode]
  );

  const currentMoneyItems = useMemo(
    () => [
      {
        id: 'invested',
        label: 'Инвестировано',
        money: myMoney.current.invested,
        tooltip: MY_MONEY_TOOLTIPS.currentInvested,
      },
      {
        id: 'income-paid',
        label: 'Возвращено',
        money: myMoney.current.incomePaid,
        tooltip: MY_MONEY_TOOLTIPS.currentIncomePaid,
      },
      {
        id: 'income-left',
        label: 'Прогноз возврата',
        money: myMoney.current.incomeLeft,
        tooltip: MY_MONEY_TOOLTIPS.currentIncomeLeft,
        accent: true,
      },
    ],
    [myMoney.current]
  );

  const currentRateItems = useMemo(
    () => [
      {
        id: 'roi',
        label: 'Прогноз ROI',
        value: formatRate(myMoney.current.forecastSimpleInterestRate),
        tooltip: MY_MONEY_TOOLTIPS.currentForecastROI,
      },
      {
        id: 'year-roi',
        label: 'Прогноз годового ROI',
        value: formatRate(myMoney.current.forecastYearROI),
        tooltip: MY_MONEY_TOOLTIPS.currentForecastYearROI,
      },
      {
        id: 'irr',
        label: 'Прогноз годового IRR',
        value: formatRate(myMoney.current.forecastInterestRate),
        tooltip: MY_MONEY_TOOLTIPS.currentForecastIRR,
      },
      {
        id: 'term',
        label: 'Плановый срок',
        value: formatTerm(myMoney.current.averageInitialTerm),
        tooltip: MY_MONEY_TOOLTIPS.currentTerm,
      },
    ],
    [myMoney.current]
  );

  const closedMoneyItems = useMemo(
    () => [
      {
        id: 'invested',
        label: 'Инвестировано',
        money: myMoney.closed.invested,
        tooltip: MY_MONEY_TOOLTIPS.closedInvested,
      },
      {
        id: 'comeback',
        label: 'Возвращено',
        money: myMoney.closed.comebackAmount,
        tooltip: MY_MONEY_TOOLTIPS.closedComeback,
      },
      {
        id: 'net-profit',
        label: 'Чистая прибыль',
        money: myMoney.closed.netProfit,
        tooltip: MY_MONEY_TOOLTIPS.closedNetProfit,
        accent: true,
      },
    ],
    [myMoney.closed]
  );

  const closedRateItems = useMemo(
    () => [
      {
        id: 'roi',
        label: 'Фактический ROI',
        value: formatRate(myMoney.closed.simpleInterestRate),
        tooltip: MY_MONEY_TOOLTIPS.closedFactROI,
      },
      {
        id: 'year-roi',
        label: 'Фактический годовой ROI',
        value: formatRate(myMoney.closed.factYearROI),
        tooltip: MY_MONEY_TOOLTIPS.closedFactYearROI,
      },
      {
        id: 'irr',
        label: 'Фактический годовой IRR',
        value: formatRate(myMoney.closed.interestRate),
        tooltip: MY_MONEY_TOOLTIPS.closedFactIRR,
      },
      {
        id: 'term',
        label: 'Фактический срок',
        value: formatTerm(myMoney.closed.averageInitialTerm),
        tooltip: MY_MONEY_TOOLTIPS.closedTerm,
      },
    ],
    [myMoney.closed]
  );

  const lifetimeTotals = useMemo(
    () => [
      {
        id: 'deposit',
        label: 'Всего зачислено',
        money: myMoney.total.depositAmount,
        tooltip: MY_MONEY_TOOLTIPS.depositTotal,
      },
      {
        id: 'invested',
        label: 'Всего инвестировано',
        money: myMoney.total.invested,
        tooltip: MY_MONEY_TOOLTIPS.investedTotal,
      },
      {
        id: 'withdraw',
        label: 'Всего выведено',
        money: myMoney.total.withdrawAmount,
        tooltip: MY_MONEY_TOOLTIPS.withdrawTotal,
      },
      {
        id: 'comeback',
        label: 'Всего возвращено',
        money: myMoney.total.comebackAmount,
        tooltip: MY_MONEY_TOOLTIPS.comebackTotal,
      },
    ],
    [myMoney.total]
  );

  const isContentLocked = useMemo(() => {
    if (loading) {
      return false;
    }

    const isNotVerified = !profile.lenderVerified;
    const needContract = profile.lenderVerified && profile.agentCode === null;

    return isNotVerified || needContract;
  }, [loading, profile.lenderVerified, profile.agentCode]);

  useEffect(() => {
    handleErrorRef.current = handleError;
  }, [handleError]);

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    const onMount = async () => {
      try {
        const statsProvider = await createStatsProvider();
        const myMoneyData = await statsProvider.getStats();
        dispatch(setMoneyData(myMoneyData));

        await loadAccounts();
      } catch (error) {
        if (error instanceof Error) {
          await handleErrorRef.current(error);
        }
      } finally {
        setLoading(false);
      }
    };

    void onMount();
  }, [dispatch, loadAccounts]);

  return (
    <>
      <Container>
        <Section>
          <AccountNotReadyWarningSection />

          {loading ? (
            <div className={styles.skeleton} aria-busy="true" aria-live="polite">
              <Skeleton className={styles.skeletonItem} />
              <Skeleton className={styles.skeletonItem} />
              <Skeleton className={styles.skeletonItem} />
            </div>
          ) : (
            <>
              <div className={cn(styles.dashboard, isContentLocked && styles.contentLocked)}>
                <div className={styles.accountsColumn}>
                  <button
                    type="button"
                    className={cn(styles.card, styles.accountCard)}
                    onClick={() => navigate('account/main')}
                  >
                    <Image src={rub} alt={'rub'} className={styles.icon} />
                    <div className={styles.balanceInfo}>
                      <div className={styles.balanceAmount}>
                        <Money money={mainBalance} />
                      </div>
                      <div className={styles.balanceLabel}>основной счет</div>
                    </div>
                  </button>

                  <div className={cn(styles.card, styles.comfortAccountWrapper)}>
                    <button
                      type="button"
                      className={cn(
                        styles.accountCard,
                        !comfortAccountOpened && styles.accountCardClosed
                      )}
                      onClick={comfortAccountOpened ? () => navigate('account/comfort') : undefined}
                      disabled={!comfortAccountOpened}
                    >
                      <Image src={rub} alt={'rub'} className={styles.icon} />
                      <div className={styles.balanceInfo}>
                        <div className={styles.balanceAmount}>
                          <Money money={comfortBalance} />
                        </div>
                        <div className={styles.balanceLabel}>счет комфорт</div>
                      </div>
                    </button>

                    <button
                      className={styles.comfortActionBtn}
                      type="button"
                      disabled={isContentLocked}
                      onClick={() =>
                        comfortAccountOpened ? setTopupModalOpen(true) : setComfortModalOpen(true)
                      }
                    >
                      <PlusIcon preset={'circle-filled'} size={28} />
                      <span>
                        {comfortAccountOpened ? 'Создать новый пакет' : 'Открыть счет Комфорт'}
                      </span>
                    </button>
                  </div>

                  <button
                    className={styles.promoCard}
                    type="button"
                    onClick={() => setComfortModalOpen(true)}
                  >
                    <div className={styles.promoHeading}>
                      <h2 className={styles.promoTitle}>
                        Выгода без
                        <br />
                        ограничений с
                      </h2>
                      <div className={styles.promoBadge}>счетом Комфорт</div>
                    </div>

                    <div className={styles.promoFeatures}>
                      <div className={styles.promoFeaturesTitle}>Преимущества счета Комфорт</div>
                      <ul className={cn(styles.planFeaturesList, styles.promoFeaturesList)}>
                        {COMFORT_FEATURES.map((feature) => (
                          <li key={feature} className={styles.planFeaturesItem}>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </button>

                  <CustomLink
                    to={RouteDictionary.PROFILE}
                    className={cn(styles.card, styles.accountInfoCard)}
                  >
                    <div className={styles.profileHead}>
                      <Image src={avatar} alt={'avatar'} className={styles.profileAvatar} />
                      <div className={styles.profileInfo}>
                        <h3 className={styles.profileTitle}>Мой аккаунт</h3>
                        <div className={styles.profileMeta}>
                          <span className={styles.profileMetaLabel}>создан :</span>
                          <span className={styles.profileMetaValue}>{createdAt ?? '—'}</span>
                        </div>
                      </div>
                      <div className={styles.profileIdentity}>
                        <div className={styles.profileName}>
                          {profile.name || 'Новый пользователь'}
                        </div>
                        <div className={styles.profileMeta}>
                          <span className={styles.profileMetaValue}>ID {profile.paymentCode}</span>
                        </div>
                      </div>
                    </div>

                    <div className={styles.profileLinkRow}>
                      <span className={styles.profileLinkText}>Перейти в настройки</span>
                      <span className={styles.profileLinkArrow}>
                        <ArrowIcon direction={'right'} size={20} />
                      </span>
                    </div>
                  </CustomLink>
                </div>

                <div className={styles.statsColumn}>
                  <section className={cn(styles.card, styles.totalsCard)}>
                    {lifetimeTotals.map((item) => (
                      <div key={item.id} className={styles.totalItem}>
                        <div className={styles.totalValue}>
                          <Money money={item.money} withPennies={false} />
                        </div>
                        <Tooltip content={item.tooltip}>
                          <span className={styles.totalLabel}>{item.label}</span>
                        </Tooltip>
                      </div>
                    ))}
                  </section>

                  <section className={cn(styles.card, styles.platformCard)}>
                    <div className={styles.platformSummary}>
                      <div className={styles.platformTotal}>
                        <Money money={platformTotal} withPennies={false} />
                      </div>
                      <div className={styles.cardTitle}>всего на платформе</div>
                    </div>

                    <ul className={styles.platformList}>
                      {platformItems.map((item) => (
                        <li key={item.id} className={styles.platformListRow}>
                          <Tooltip content={item.tooltip}>
                            <span className={styles.platformListLabel}>{item.label}</span>
                          </Tooltip>
                          <span className={styles.platformListValue}>
                            <Money
                              money={{ amount: item.amount, currencyCode }}
                              withPennies={false}
                            />
                          </span>
                        </li>
                      ))}
                    </ul>
                  </section>
                  <MyMoneyPortfolioCard
                    title="текущие проекты"
                    projects={myMoney.current.projects}
                    moneyItems={currentMoneyItems}
                    rateItems={currentRateItems}
                  />

                  <MyMoneyPortfolioCard
                    title="завершённые проекты"
                    projects={myMoney.closed.projects}
                    moneyItems={closedMoneyItems}
                    rateItems={closedRateItems}
                  />

                  <section className={cn(styles.card, styles.myAccountCard, styles.premiumRow)}>
                    {isPremium ? (
                      <div className={styles.premiumAccount}>
                        <div className={styles.progressLabels}>
                          <div className={styles.userInfo}>
                            <Image src={avatar} alt={'avatar'} className={styles.avatar} />
                            <div className={styles.premiumUserId}>ID {profile.paymentCode}</div>
                          </div>
                          <div className={styles.progressLabel}>
                            <span className={styles.progressLabelText}>Инвестировано:</span>
                            <span className={styles.progressAmount}>
                              <Money money={myMoney.current.invested} withPennies={false} />
                            </span>
                          </div>
                        </div>

                        <div className={styles.premiumBannerRow}>
                          <div className={styles.premiumBanner}>
                            <div className={styles.premiumBannerTitle}>Премиум</div>
                            <div className={styles.premiumBannerDesc}>
                              обслуживание уровня
                              <br />
                              private banking
                            </div>
                          </div>

                          <div className={styles.premiumAdvantages}>
                            <div className={styles.premiumAdvantagesTitle}>
                              Преимущества премиум аккаунта
                            </div>
                            <div className={styles.premiumAdvantagesList}>
                              <div className={styles.featureItem}>
                                <CheckIcon preset={'circle-success'} size={24} />
                                Резервирование участия в сделке
                              </div>
                              <div className={styles.featureItem}>
                                <CheckIcon preset={'circle-success'} size={24} />
                                Личный менеджер
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className={styles.progressSection}>
                          <div className={styles.progressLabels}>
                            <div className={styles.progressLabel}>
                              <span className={styles.progressLabelText}>
                                Инвестировано в текущие проекты:
                              </span>
                              <span className={styles.progressAmount}>
                                <Money money={myMoney.current.invested} withPennies={false} />
                              </span>
                            </div>
                            <div className={cn(styles.progressLabel, styles.progressLabelRight)}>
                              <span className={styles.progressLabelText}>Осталось до премиум:</span>
                              <span className={styles.progressAmount}>
                                <Money money={premiumRemaining} withPennies={false} />
                              </span>
                            </div>
                          </div>
                          <ProgressBar lines={premiumProgressLines} />
                        </div>

                        <div className={styles.statusCards}>
                          <div className={cn(styles.statusCard, styles.standardCard)}>
                            <div className={styles.statusName}>Стандарт</div>
                            <div className={styles.statusDesc}>базовый функционал платформы</div>
                            <ul className={styles.planFeaturesList}>
                              {STANDARD_PLAN_FEATURES.map((feature) => (
                                <li key={feature.id} className={styles.planFeaturesItem}>
                                  {feature.content}
                                </li>
                              ))}
                            </ul>
                          </div>

                          <div className={cn(styles.statusCard, styles.premiumCard)}>
                            <div className={styles.statusNameGray}>Премиум</div>
                            <div className={styles.statusDescGray}>
                              обслуживание уровня private banking
                            </div>
                            <ul
                              className={cn(
                                styles.planFeaturesList,
                                styles.planFeaturesListGray,
                                styles.planFeaturesListChecked
                              )}
                            >
                              {PREMIUM_PLAN_FEATURES.map((feature) => (
                                <li key={feature.id} className={styles.planFeaturesItem}>
                                  <CheckIcon preset={'circle-success'} size={20} />
                                  {feature.content}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </>
                    )}
                  </section>
                </div>
              </div>
            </>
          )}
        </Section>
      </Container>
      <ComfortAccountWizardModal
        accountOpened={comfortAccountOpened}
        active={comfortModalOpen}
        onClose={() => setComfortModalOpen(false)}
        onAccountOpened={() => {
          void loadAccounts();
        }}
        onTopup={() => setTopupModalOpen(true)}
      />
      <ComfortAccountTopupModal
        active={topupModalOpen}
        onClose={() => setTopupModalOpen(false)}
        availableOnMainAccount={mainBalance}
        onSuccess={() => {
          void loadAccounts();
        }}
      />
    </>
  );
};
